// SHA-256 for JavaScript - Optimized implementation
import type { AsyncSequence } from "../types/AsyncSequence"
import type { GUID } from "../types/GUID"

/**
 * Computes SHA-256 hash of byte stream.
 *
 * Optimized JavaScript implementation supporting streaming input.
 * Returns hash as 256-bit bigint (GUID).
 *
 * @param input - Async sequence of Uint8Array chunks
 * @returns Promise resolving to GUID hash (256-bit bigint)
 *
 * @example
 * ```ts
 * import { UInt8Hash } from "@pdfking/tfunc"
 *
 * const hash = await UInt8Hash([new Uint8Array([1, 2, 3])])
 * ```
 */

// SHA-256 round constants
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

// Reusable message schedule (16 words, circular)
const W = new Uint32Array(16)

// Rotation function
const ROTR = (x: number, n: number): number => (x >>> n) | (x << (32 - n))

// Byte swap for buffer operations
const bswap = (x: number): number =>
  ((x >>> 24) | ((x >>> 8) & 0xff00) | ((x << 8) & 0xff0000) | (x << 24)) >>> 0

// Compress block from Uint32Array (for internal buffer)
const compressU32 = (
  state: Int32Array,
  u32: Uint32Array,
  off: number
): void => {
  let a = state[0],
    b = state[1],
    c = state[2],
    d = state[3],
    e = state[4],
    f = state[5],
    g = state[6],
    h = state[7]

  for (let i = 0; i < 16; i++) {
    const w = bswap(u32[off + i])
    W[i] = w

    const s1 = ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25)
    const ch = (e & f) ^ (~e & g)
    const t1 = (h + s1 + ch + K[i] + w) | 0
    const s0 = ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22)
    const maj = (a & b) | (c & (a | b))
    const t2 = (s0 + maj) | 0
    h = g
    g = f
    f = e
    e = (d + t1) | 0
    d = c
    c = b
    b = a
    a = (t1 + t2) | 0
  }

  for (let i = 16; i < 64; i++) {
    const i16 = i & 15
    const w2 = W[(i - 2) & 15],
      w7 = W[(i - 7) & 15],
      w15 = W[(i - 15) & 15],
      w16 = W[i16]
    const sig0 = ROTR(w15, 7) ^ ROTR(w15, 18) ^ (w15 >>> 3)
    const sig1 = ROTR(w2, 17) ^ ROTR(w2, 19) ^ (w2 >>> 10)
    const w = (w16 + sig0 + w7 + sig1) | 0
    W[i16] = w
    const s1 = ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25)
    const ch = (e & f) ^ (~e & g)
    const t1 = (h + s1 + ch + K[i] + w) | 0
    const s0 = ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22)
    const maj = (a & b) | (c & (a | b))
    const t2 = (s0 + maj) | 0
    h = g
    g = f
    f = e
    e = (d + t1) | 0
    d = c
    c = b
    b = a
    a = (t1 + t2) | 0
  }

  state[0] = (state[0] + a) | 0
  state[1] = (state[1] + b) | 0
  state[2] = (state[2] + c) | 0
  state[3] = (state[3] + d) | 0
  state[4] = (state[4] + e) | 0
  state[5] = (state[5] + f) | 0
  state[6] = (state[6] + g) | 0
  state[7] = (state[7] + h) | 0
}

// Compress block from DataView (for input data)
const compressDV = (state: Int32Array, dv: DataView, off: number): void => {
  let a = state[0],
    b = state[1],
    c = state[2],
    d = state[3],
    e = state[4],
    f = state[5],
    g = state[6],
    h = state[7]

  for (let i = 0; i < 16; i++) {
    const w = dv.getUint32(off + i * 4, false)
    W[i] = w
    const s1 = ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25)
    const ch = (e & f) ^ (~e & g)
    const t1 = (h + s1 + ch + K[i] + w) | 0
    const s0 = ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22)
    const maj = (a & b) | (c & (a | b))
    const t2 = (s0 + maj) | 0
    h = g
    g = f
    f = e
    e = (d + t1) | 0
    d = c
    c = b
    b = a
    a = (t1 + t2) | 0
  }

  for (let i = 16; i < 64; i++) {
    const i16 = i & 15
    const w2 = W[(i - 2) & 15],
      w7 = W[(i - 7) & 15],
      w15 = W[(i - 15) & 15],
      w16 = W[i16]
    const sig0 = ROTR(w15, 7) ^ ROTR(w15, 18) ^ (w15 >>> 3)
    const sig1 = ROTR(w2, 17) ^ ROTR(w2, 19) ^ (w2 >>> 10)
    const w = (w16 + sig0 + w7 + sig1) | 0
    W[i16] = w
    const s1 = ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25)
    const ch = (e & f) ^ (~e & g)
    const t1 = (h + s1 + ch + K[i] + w) | 0
    const s0 = ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22)
    const maj = (a & b) | (c & (a | b))
    const t2 = (s0 + maj) | 0
    h = g
    g = f
    f = e
    e = (d + t1) | 0
    d = c
    c = b
    b = a
    a = (t1 + t2) | 0
  }

  state[0] = (state[0] + a) | 0
  state[1] = (state[1] + b) | 0
  state[2] = (state[2] + c) | 0
  state[3] = (state[3] + d) | 0
  state[4] = (state[4] + e) | 0
  state[5] = (state[5] + f) | 0
  state[6] = (state[6] + g) | 0
  state[7] = (state[7] + h) | 0
}

// Process full blocks from a chunk
const compressBlocks = (
  state: Int32Array,
  chunk: Uint8Array,
  off: number,
  len: number
): number => {
  const dv = new DataView(chunk.buffer, chunk.byteOffset + off, len)
  let pos = 0
  const fullBlocks = len & ~63
  while (pos < fullBlocks) {
    compressDV(state, dv, pos)
    pos += 64
  }
  return fullBlocks
}

/**
 * Computes SHA-256 hash of byte stream.
 * Returns hash as 256-bit bigint (GUID).
 */
export const UInt8Hash = async (
  input: AsyncSequence<Uint8Array>
): Promise<GUID> => {
  const state = new Int32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ])

  // Buffer for partial blocks and padding (128 bytes for final padding)
  const buf32 = new Uint32Array(32)
  const buf8 = new Uint8Array(buf32.buffer)
  let bufLen = 0
  let total = 0

  for await (const chunk of input) {
    const len = chunk.length
    total += len

    let pos = 0

    // Fill partial buffer first
    if (bufLen > 0) {
      const need = 64 - bufLen
      if (len < need) {
        buf8.set(chunk, bufLen)
        bufLen += len
        continue
      }
      buf8.set(chunk.subarray(0, need), bufLen)
      compressU32(state, buf32, 0)
      bufLen = 0
      pos = need
    }

    // Process full blocks directly from chunk
    const remaining = len - pos
    const processed = compressBlocks(state, chunk, pos, remaining)
    pos += processed

    // Buffer remainder
    if (pos < len) {
      buf8.set(chunk.subarray(pos), 0)
      bufLen = len - pos
    }
  }

  // Padding
  const bitLenHi = (total / 0x20000000) | 0
  const bitLenLo = total << 3
  const padLen = total % 64 < 56 ? 64 : 128

  buf8[bufLen] = 0x80
  buf8.fill(0, bufLen + 1, padLen - 8)
  buf32[(padLen >>> 2) - 2] = bswap(bitLenHi)
  buf32[(padLen >>> 2) - 1] = bswap(bitLenLo)

  compressU32(state, buf32, 0)
  if (padLen === 128) compressU32(state, buf32, 16)

  // Convert to bigint
  let hash = 0n
  for (let i = 0; i < 8; i++) {
    hash = (hash << 32n) | BigInt(state[i] >>> 0)
  }
  return hash as GUID
}
